import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { CreditsData, PlanData } from '@/hooks/use-plan-and-credits'
import { formatCurrency, formatInterval, formatNumber } from '@/lib/utils'
import type { Subscription } from '@/types'
import { Link } from '@tanstack/react-router'
import { Calendar, CheckCircle, DollarSign, Zap } from 'lucide-react'

type PlanCreditsSummaryProps = {
  currentPlan: PlanData | null
  creditsData: CreditsData
  subscription?: Subscription | null
  manageHref?: string
  manageLabel?: string
}

export function PlanCreditsSummary({
  currentPlan,
  creditsData,
  subscription,
  manageHref = '/dashboard/billing',
  manageLabel = 'Manage Plan',
}: PlanCreditsSummaryProps) {
  const title = currentPlan ? 'Current Plan' : 'Free Credits'
  const badge = currentPlan?.name || 'Trial'
  const description = currentPlan?.description || 'You have free credits to get started'
  const nextBilling = subscription?.current_period_end
    ? new Date(Number(subscription.current_period_end) * 1000).toLocaleDateString()
    : null

  return (
    <Card className="overflow-hidden rounded-[1.75rem] border-0 bg-[#1f2937] text-white shadow-sm">
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-white">
              {title}
              <Badge variant="secondary" className="border-white/30 bg-white/20 text-white">
                {badge}
              </Badge>
            </CardTitle>
            <CardDescription className="max-w-2xl text-white/75">
              {description}
            </CardDescription>
          </div>
          <Button variant="secondary" size="sm" className="rounded-full" asChild>
            <Link to={manageHref}>{manageLabel}</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm sm:p-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">API Credits</h3>
                  <p className="text-sm text-white/70">Your available usage</p>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <div className="text-3xl font-bold text-white">
                  {creditsData.unlimited ? '∞' : formatNumber(creditsData.remaining)}
                </div>
                <div className="text-sm font-medium text-white/75">
                  {creditsData.unlimited ? 'Unlimited' : 'Credits Left'}
                </div>
              </div>
            </div>

            <div className="mt-4 border-t border-white/20 pt-4">
              <div className="flex items-center gap-2 text-sm text-white/75">
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-200" />
                {creditsData.unlimited
                  ? 'No usage limits'
                  : creditsData.remaining > 5000
                    ? 'Plenty of credits available'
                    : creditsData.remaining > 500
                      ? 'Good amount remaining'
                      : 'Consider topping up soon'}
              </div>
            </div>
          </div>

          {currentPlan && (currentPlan.price > 0 || nextBilling) && (
            <div className="grid gap-3 md:grid-cols-2">
              {currentPlan.price > 0 && (
                <div className="flex items-center justify-between rounded-2xl bg-white/10 p-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-white/70" />
                    <div>
                      <div className="font-medium text-white">
                        {formatCurrency(currentPlan.price, currentPlan.currency)}
                      </div>
                      <div className="text-xs text-white/70">per {formatInterval(currentPlan.interval)}</div>
                    </div>
                  </div>
                </div>
              )}

              {nextBilling && (
                <div className="flex items-center justify-between rounded-2xl bg-white/10 p-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-white/70" />
                    <div>
                      <div className="font-medium text-white">Next Billing</div>
                      <div className="text-xs text-white/70">{nextBilling}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
